<script setup lang="ts">

import ItemV3ParameterBox from "@/components/itemV3/parameter/itemV3ParameterBox.vue";
import ItemV3MostSelected from "@/components/itemV3/parameter/itemV3MostSelected.vue";
import {computed, inject, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import {getCurrentText} from "@/assets/ts/itemText";

const {t} = useI18n();

const emit = defineEmits(["patch"]);
const props = defineProps(["item"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

const imageWidthPercent: number = 30
const loading = ref(false)

/**
 * Parameter box header images
 */

const mainMetal = computed(() => store.getters["session/getMainMetal"])
const boxIdx = "width"

const getHeaderImage = (size: string = "600x300", withBg = false) => {
  if (withBg) {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${mainMetal.value}_${boxIdx}.webp`
  } else {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${boxIdx}.webp`
  }
}

/**
 * session
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = "width"

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

const open = ref(false)

const variants = computed(() => open.value ? parametersVariations.getVariants() : [])

/**
 * color variants
 */

const activeIdx = ref(0)

const variantsList = computed(() => {
  if (!variants.value.length || variants.value.length < activeIdx.value) return []
  return variants.value[activeIdx.value].values
})

const active = computed(() => {
  if (!variants.value.length || variants.value.length < activeIdx.value) return []
  return variants.value[activeIdx.value]
})

const matchedIdx = computed(() => {
  if (!variants.value.length || variants.value.length < activeIdx.value) return 0
  return variants.value[activeIdx.value].values.findIndex((v: any) => v.match)
})

const currentWidthText = computed(() => {
  return getCurrentText(configureAll.value ? -1 : itemIdx.value, variantIdx)
})

/*
 * focus values
 */

const focusValueIdx = [
    // idx must be a string with one decimal digit
  "2.5", "3.0", "3.5", "4.0", "5.0"
]

const formatNumber = (num: number) => {
  return Number(num).toFixed(1)
}

const focusValueIdxCount = 5

const getFocusValueIdx = computed(
    () => {
      let focusList = focusValueIdx

      if (matchedIdx.value && matchedIdx.value != -1) {
        if (!focusValueIdx.includes(formatNumber(variants.value[activeIdx.value].values[matchedIdx.value].idx))) {
          focusList = [
            formatNumber(variants.value[activeIdx.value].values[matchedIdx.value].idx),
            ...focusValueIdx.slice(0, -1)
          ]
        }
      }

      const availableFocus = variantsList.value
          .map((v: any) => formatNumber(v.idx))
          .filter((idx: string) => focusList.includes(idx))

      if (availableFocus.length < focusValueIdxCount) {
        const nextNonFocus = variantsList.value
            .map((v: any) => formatNumber(v.idx))
            .filter((idx: string) => !availableFocus.includes(idx))

        availableFocus.push(...nextNonFocus.slice(0, focusValueIdxCount - availableFocus.length))
      }

      return availableFocus
    }
)

/**
 * image
 */

const hoverIdx = ref(-1)

const setHoverIdx = () => {
  if (matchedIdx.value !== -1 && variantsList.value.length >= matchedIdx.value) {
    hoverIdx.value = matchedIdx.value
  }
}

watch(active, () => {
  setHoverIdx()
})

watch(props.item, () => {
  setHoverIdx()
})

const imageUrl = computed(() => parametersVariations.imageUrl(variantsList.value, hoverIdx.value))

const getWidthImageUrl = (idx: number | string, size: string = "300x300") => {
  if (idx === undefined || idx === null) return ''
  const width = formatNumber(idx).replace('.', '')
  return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/width/${size}/${width}.webp`
}

const checkImageLoaded = (url: string) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(true);
    img.src = url;
  });
};

const checkAllLoaded = async () => {
  const profiles = variants.value;

  if (!profiles.length) return;
  const allListsCreated = profiles.every((profile: any) => profile.values && profile.values.length > 0);
  if (!allListsCreated) return;

  const imageUrls = profiles.flatMap((disc: any) => disc.values
      .filter((profile: any) => getFocusValueIdx.value.includes(formatNumber(profile.idx)))
      .map((profile: any) => getWidthImageUrl(profile.idx)));
  await Promise.all(imageUrls.map((url: string) => checkImageLoaded(url)));

  //loading.value = false;
};

watch(variants, () => {
  checkAllLoaded();
  if (variants.value.length > 0) {
    //loading.value = true;
  }
}, {immediate: true, deep: true});

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * tip texts
 */

const getTipTitle = computed(() => {
  if (active.value && active.value.values[hoverIdx.value]) {
    const key = Number(active.value.values[hoverIdx.value].idx).toFixed(1).replace('.', '')
    return t('components.parameters.tips.width.' + key + '.title')
  } else {
    return ''
  }
})

const getTipText = computed(() => {
  if (active.value && active.value.values[hoverIdx.value]) {
    const key = Number(active.value.values[hoverIdx.value].idx).toFixed(1).replace('.', '')
    return t('components.parameters.tips.width.' + key + '.text')
  } else {
    return ''
  }
})

const getTipImageUrl = computed(() => {
  if (active.value && active.value.values[hoverIdx.value]) {
    const [intPart, decPart] = Number(active.value.values[hoverIdx.value].idx).toFixed(1).split('.')
    const key = `${intPart.padStart(2, '0')}${decPart}`
    return 'https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/tips/width/600x600/' + key + '_polished_yellow_hand.webp'

  } else {
    return ''
  }
})

onMounted(
    () => {
      setHoverIdx()
    }
)

</script>

<template>
  <item-v3-parameter-box
      v-model:open="open"
      :image-width-percent=imageWidthPercent
      :image=imageUrl
      :header-image="getHeaderImage()"
      :header-image-srcset="getHeaderImage() + ' 300w, ' + getHeaderImage('1200x600') +' 600w'"
      title="Breite"
      :subtitle="currentWidthText || 'Wählen Sie die Breite für Ihre Trauringe'"
      :loading=loading
      :tip-title="getTipTitle"
      :tip-text="getTipText"
      :tip-image="getTipImageUrl"
  >
    <template v-slot:main>

      <!-- most selected -->
      <item-v3-most-selected/>
      <ul class="w-full flex gap-x-1 flex-wrap mb-2 justify-center">
        <!-- disc list -->
        <template v-for="(variantData, variantIdx) in variantsList">
          <li :key="variantIdx"
              v-if="getFocusValueIdx.includes(formatNumber(variantData.idx))"
              class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
              @click="handleValuePatch(active.idx, variantData)"
              @mouseover="hoverIdx = variantIdx as number"
              @mouseleave="hoverIdx = matchedIdx"
              :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
              'border-2 border-transparent': !variantData.match
            }"
          >
            <div
                class="w-12 h-12 relative flex rounded-2xl overflow-hidden border-r border-gray-300">
              <img
                  :src="getWidthImageUrl(variantData.idx)"
                  :srcset="getWidthImageUrl(variantData.idx, '300x300') + ' 300w, ' + getWidthImageUrl(variantData.idx, '600x600') + ' 600w'"
                  loading="lazy"
                  class="relative h-34 -top-13 w-auto max-w-none object-cover object-left"
              >

            </div>
            <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
            {{ variantData.title }}
          </span>
          </li>
        </template>
      </ul>
    </template>
    <template v-slot:more>

      <ul class="flex gap-x-1 flex-wrap mb-2 w-full justify-center ">
        <!-- disc list -->
        <template v-for="(variantData, variantIdx) in variantsList">
          <li :key="variantIdx"
              v-if="!getFocusValueIdx.includes(formatNumber(variantData.idx))"
              class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
              @click="handleValuePatch(active.idx, variantData)"
              @mouseover="hoverIdx = variantIdx as number"
              @mouseleave="hoverIdx = matchedIdx"
              :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
              'border-2 border-transparent': !variantData.match
            }"
          >
            <div
                class="w-12 h-12 relative flex rounded-2xl overflow-hidden border-r border-gray-300">
              <img
                  :src="getWidthImageUrl(variantData.idx)"
                  :srcset="getWidthImageUrl(variantData.idx, '300x300') + ' 300w, ' + getWidthImageUrl(variantData.idx, '600x600') + ' 600w'"
                  loading="lazy"
                  class="relative h-32 -top-11 w-auto max-w-none object-cover object-left"
              >

            </div>
            <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
            {{ variantData.title }}
          </span>
          </li>
        </template>
      </ul>
    </template>
  </item-v3-parameter-box>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>